import React, { useState, useRef, useEffect, useCallback } from 'react'
import { FiPlay, FiPause, FiMinus, FiPlus } from 'react-icons/fi'
import { motion } from 'framer-motion'

export default function MetronomeV2() {
  const [bpm, setBpm] = useState(120)
  const [isPlaying, setIsPlaying] = useState(false)
  const [timeSignature, setTimeSignature] = useState(4)
  const [currentBeat, setCurrentBeat] = useState(0)

  const audioContextRef = useRef<AudioContext | null>(null)
  const schedulerTimerRef = useRef<number | null>(null)
  const nextNoteTimeRef = useRef(0)
  const beatInBarRef = useRef(0)

  const lookahead = 25 // ms
  const scheduleAheadTime = 0.10 // sec

  // Post resize message for iframe embedding
  const postResize = useCallback(() => {
    try {
      window.parent?.postMessage({ type: 'resize', height: 600 }, '*')
    } catch (e) {
      // Ignore if not in iframe
    }
  }, [])

  useEffect(() => {
    postResize()
  }, [postResize])

  const scheduleNote = useCallback((beatNumber: number, time: number) => {
    const ctx = audioContextRef.current
    if (!ctx) return

    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    
    const isDownbeat = (beatNumber % timeSignature) === 0
    osc.type = 'sine'
    osc.frequency.setValueAtTime(isDownbeat ? 880 : 440, time)
    gain.gain.setValueAtTime(0.3, time)
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05)
    
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start(time)
    osc.stop(time + 0.05)

    // Visual update aligned with audio time
    const msUntil = (time - ctx.currentTime) * 1000
    window.setTimeout(() => {
      setCurrentBeat(beatNumber % timeSignature)
    }, Math.max(0, msUntil))
  }, [timeSignature])

  const nextNote = useCallback(() => {
    const secondsPerBeat = 60 / bpm
    nextNoteTimeRef.current += secondsPerBeat
    beatInBarRef.current = (beatInBarRef.current + 1) % timeSignature
  }, [bpm, timeSignature])

  const scheduler = useCallback(() => {
    const ctx = audioContextRef.current
    if (!ctx) return
    
    while (nextNoteTimeRef.current < ctx.currentTime + scheduleAheadTime) {
      scheduleNote(beatInBarRef.current, nextNoteTimeRef.current)
      nextNote()
    }
    schedulerTimerRef.current = window.setTimeout(scheduler, lookahead) as unknown as number
  }, [nextNote, scheduleNote])

  useEffect(() => {
    if (!isPlaying) {
      if (schedulerTimerRef.current) clearTimeout(schedulerTimerRef.current)
      setCurrentBeat(0)
      return
    }

    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
    }
    
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume()
    }

    beatInBarRef.current = 0
    nextNoteTimeRef.current = audioContextRef.current.currentTime + 0.10
    scheduler()

    return () => {
      if (schedulerTimerRef.current) clearTimeout(schedulerTimerRef.current)
    }
  }, [isPlaying, bpm, timeSignature, scheduler])

  useEffect(() => {
    return () => {
      if (schedulerTimerRef.current) clearTimeout(schedulerTimerRef.current)
    }
  }, [])

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying)
  }

  const handleBpmChange = (newBpm: number) => {
    const clampedBpm = Math.max(40, Math.min(240, newBpm))
    setBpm(clampedBpm)
  }

  const handleTimeSignatureChange = (newTimeSignature: number) => {
    if (!isPlaying) {
      setTimeSignature(newTimeSignature)
      setCurrentBeat(0)
    }
  }

  return (
    <div className="w-full max-w-md mx-auto p-6 bg-white rounded-xl shadow-lg">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Metronome</h1>
        <div className="text-lg text-gray-600">
          {bpm} BPM • {timeSignature}/4
        </div>
      </div>

      {/* Beat Indicators */}
      <div className="flex justify-center mb-8 space-x-2">
        {Array.from({ length: timeSignature }).map((_, index) => (
          <motion.div
            key={index}
            className={`w-4 h-4 rounded-full border-2 transition-colors ${
              currentBeat === index && isPlaying
                ? index === 0
                  ? 'bg-red-500 border-red-500'
                  : 'bg-blue-500 border-blue-500'
                : 'bg-gray-200 border-gray-300'
            }`}
            animate={{
              scale: currentBeat === index && isPlaying ? [1, 1.3, 1] : 1
            }}
            transition={{ duration: 0.1 }}
          />
        ))}
      </div>

      {/* BPM Control */}
      <div className="mb-8">
        <div className="text-center mb-4">
          <div className="text-4xl font-bold text-gray-900 mb-2">{bpm}</div>
          <div className="text-sm text-gray-500">BPM</div>
        </div>
        
        <div className="flex items-center space-x-4 mb-4">
          <button
            onClick={() => handleBpmChange(bpm - 1)}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg focus-ring"
            aria-label="Decrease BPM"
          >
            <FiMinus className="w-4 h-4" />
          </button>
          
          <input
            type="range"
            min="40"
            max="240"
            value={bpm}
            onChange={(e) => handleBpmChange(parseInt(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
          />
          
          <button
            onClick={() => handleBpmChange(bpm + 1)}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg focus-ring"
            aria-label="Increase BPM"
          >
            <FiPlus className="w-4 h-4" />
          </button>
        </div>

        <div className="flex justify-between text-xs text-gray-500">
          <span>40</span>
          <span>240</span>
        </div>
      </div>

      {/* Time Signature */}
      <div className="mb-8">
        <div className="text-sm font-medium text-gray-700 mb-3">Time Signature</div>
        <div className="flex space-x-2">
          {[2, 3, 4, 5, 6].map((sig) => (
            <button
              key={sig}
              onClick={() => handleTimeSignatureChange(sig)}
              disabled={isPlaying}
              className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors focus-ring ${
                timeSignature === sig
                  ? 'bg-blue-600 text-white'
                  : isPlaying
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {sig}/4
            </button>
          ))}
        </div>
      </div>

      {/* Play/Pause Button */}
      <div className="text-center">
        <motion.button
          onClick={handlePlayPause}
          whileTap={{ scale: 0.95 }}
          className={`w-20 h-20 rounded-full flex items-center justify-center text-white text-2xl shadow-lg focus-ring transition-colors ${
            isPlaying
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
          aria-label={isPlaying ? 'Stop metronome' : 'Start metronome'}
        >
          {isPlaying ? <FiPause /> : <FiPlay className="ml-1" />}
        </motion.button>
      </div>

      {/* Quick BPM Presets */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="text-sm font-medium text-gray-700 mb-3">Quick Tempos</div>
        <div className="grid grid-cols-4 gap-2">
          {[60, 80, 120, 140].map((preset) => (
            <button
              key={preset}
              onClick={() => handleBpmChange(preset)}
              className="py-2 px-3 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg focus-ring transition-colors"
            >
              {preset}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}